import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { atom, useAtom, useAtomValue } from 'jotai';
import WebMercatorViewport from 'viewport-mercator-project';
import type { MapRef } from 'react-map-gl';
import type { Polygon } from 'geojson';
import { InlineLoading, Checkbox } from '@carbon/react';
import { Map, PolygonArea } from 'components/map';
import mapStyle from 'components/map/map.module.scss';
import { WellMapLayer } from 'components/well-map-layer';
import { VisibleExtentEmitter } from 'components/query-map/visible-extent-emitter';
import { DrawAoiControl } from 'components/query-map/draw-aoi-control';
import {
  buildPolygonStyle,
  calculateBounds,
  extractCoordinatesFromGeometry,
} from 'lib/map';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { useComparisonWells, useComparisonUnits } from './hooks';
import { buildComparisonMapConfigAtom, MATCH_COLORS } from './map-config';
import {
  mapSpatialFilterAtom,
  colorRulesAtom,
  aoiPolygonAtom,
  aoiEditingAtom,
  zoomToPropertyAtom,
} from './atoms';
import { resolveRuleColor } from './color-by';
import { wellInPolygon } from './spatial-filter';
import { format0 } from 'utils/number';
import type {
  ComparisonResult,
  MatchStatus,
  UnitVersionResponse,
  WellQueryResult,
} from './types';
import style from './comparison-map.module.scss';

// Well sub-layers keyed by Origin/Target membership, mirroring the sidebar
// "Origin vs Target" legend. The swatch color is driven from MATCH_COLORS (the
// same source as the map fills) via the --compare-swatch custom property.
const WELL_LAYERS: { status: MatchStatus; label: string }[] = [
  { status: 'matched', label: 'In Both' },
  { status: 'unmatched_a', label: 'Only in Origin' },
  { status: 'unmatched_b', label: 'Only in Target' },
];

const MAP_HEIGHT = 420;
const VIEWPORT_PADDING = 48;
const MAX_ZOOM = 14;

// Neutral development-area boundary drawn under the wells for context; the same
// color drives the "Units" layer swatch (single source of truth).
const UNIT_COLOR = '#8d8d8d';
const UNIT_STYLE = buildPolygonStyle({
  color: UNIT_COLOR,
  fillOpacity: 0.12,
  lineOpacity: 0.5,
  lineWidth: 1,
});

interface ComparisonMapProps {
  results: ComparisonResult[];
  isLoading: boolean;
}

/**
 * Map section for the comparison results page. Renders every slot's well
 * geometry on the shared Mapbox map, colored by Origin/Target membership
 * (blue = in both, brown = only Origin, green = only Target), over neutral
 * development-area unit boundaries for context. Collapsible, mirroring the
 * other result sections. The sidebar's Global Map Filter (Visible Extent / Draw
 * AOI) clips which wells are shown.
 * @param {ComparisonMapProps} props - The comparison results and loading flag
 * @return {JSX.Element} The map section
 */
export const ComparisonMap = ({ results, isLoading }: ComparisonMapProps) => {
  const [expanded, setExpanded] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const { wells, totalWithUwi, requestedCount } = useComparisonWells(results);
  const units = useComparisonUnits(results);

  // Scoped layer toggles for the map's own geometry (per map instance),
  // surfaced in the Layers control alongside the Visualization picker
  // (mirrors SubtractMap). Wells split by Origin/Target membership. These are
  // consumed only here, so plain component state (not Jotai atoms).
  const [wellVisibility, setWellVisibility] = useState<
    Record<MatchStatus, boolean>
  >({
    matched: true,
    unmatched_a: true,
    unmatched_b: true,
  });
  const [showUnits, setShowUnits] = useState(true);

  const scopedLayers = useMemo(
    () => (
      <div className={mapStyle.groupChecks}>
        {WELL_LAYERS.map(({ status, label }) => (
          <div
            key={status}
            className={`${mapStyle.coloredFilter} ${style.swatchLayer}`}
            style={
              { '--compare-swatch': MATCH_COLORS[status] } as CSSProperties
            }
          >
            <Checkbox
              id={`compare-result-wells-${status}`}
              labelText={label}
              checked={wellVisibility[status]}
              onChange={(_evt: any, { checked }: { checked: boolean }) =>
                setWellVisibility((prev) => ({ ...prev, [status]: checked }))
              }
            />
          </div>
        ))}
        <div
          className={`${mapStyle.coloredFilter} ${style.swatchLayer}`}
          style={{ '--compare-swatch': UNIT_COLOR } as CSSProperties}
        >
          <Checkbox
            id="compare-result-units-layer"
            labelText="Units"
            checked={showUnits}
            onChange={(_evt: any, { checked }: { checked: boolean }) =>
              setShowUnits(checked)
            }
          />
        </div>
      </div>
    ),
    [wellVisibility, showUnits]
  );

  // Global Map Filter: the sidebar picks the mode; the active clipping polygon
  // comes from the viewport (extent) or the user-drawn AOI.
  const spatialFilter = useAtomValue(mapSpatialFilterAtom);
  const [extentPolygon, setExtentPolygon] = useState<Polygon | null>(null);
  // AOI polygon + vertex-editing flag are shared with the sidebar's applied-AOI
  // card (Edit / Clear), so both surfaces drive the same drawn geometry.
  const [aoiPolygon, setAoiPolygon] = useAtom(aoiPolygonAtom);
  const [aoiEditing, setAoiEditing] = useAtom(aoiEditingAtom);

  // Color by Attribute: wells take the color of the first enabled map rule they
  // match, falling back to Origin/Target membership.
  const colorRules = useAtomValue(colorRulesAtom);
  const mapRules = useMemo(
    () => colorRules.filter((r) => r.enabled && r.applyToMap),
    [colorRules]
  );
  // `Map` here is the map component (imported above), so use a plain lookup.
  const resultByUwi = useMemo(() => {
    const lookup: Record<string, ComparisonResult> = {};
    results.forEach((r) => {
      if (r.uwi && !(r.uwi in lookup)) lookup[r.uwi] = r;
    });
    return lookup;
  }, [results]);
  const wellColor = (
    uwi: string,
    matchStatus: ComparisonResult['matchStatus']
  ) => {
    const result = resultByUwi[uwi];
    const ruleColor = result ? resolveRuleColor(result, mapRules) : null;
    return ruleColor ?? MATCH_COLORS[matchStatus];
  };

  const filterPolygon =
    spatialFilter === 'extent'
      ? extentPolygon
      : spatialFilter === 'aoi'
        ? aoiPolygon
        : null;

  const visibleWells = useMemo(() => {
    if (!filterPolygon) return wells;
    return wells.filter(({ well }) => wellInPolygon(well, filterPolygon));
  }, [wells, filterPolygon]);

  // Per-instance map config: start from defaults but silence the global layers
  // so only the comparison wells draw (mirrors SubtractMap). Shared with the
  // per-property mini-maps via map-config.
  const mapConfigAtom = useMemo(() => buildComparisonMapConfigAtom(), []);

  // Imperative ref to THIS map (a per-instance atom, not the shared global one)
  // so the Properties list can fit the map to a chosen property without
  // disturbing the per-property mini-maps.
  const mapRefAtom = useMemo(() => atom<MapRef | null>(null), []);
  const mapRef = useAtomValue(mapRefAtom);
  const zoomRequest = useAtomValue(zoomToPropertyAtom);
  const handledZoomNonce = useRef<number | null>(null);

  // Per-property geometry for the Properties-list "zoom to on map" action: a
  // slot's well points, falling back to its development-area unit polygon.
  // Plain-object lookups — `Map` here is the map component, not the constructor.
  const coordsBySlotId = useMemo(() => {
    const wellByUwi: Record<string, WellQueryResult> = {};
    wells.forEach(({ uwi, well }) => {
      wellByUwi[uwi] = well;
    });
    const unitByName: Record<string, UnitVersionResponse> = {};
    units.forEach(({ name, unit }) => {
      unitByName[name] = unit;
    });
    const lookup: Record<string, number[][]> = {};
    results.forEach((result) => {
      const coords: number[][] = [];
      const well = result.uwi ? wellByUwi[result.uwi] : undefined;
      if (well) {
        (well.wellBores ?? []).forEach((wb) => {
          if (wb.surfaceHole) coords.push(wb.surfaceHole.coordinates);
          if (wb.bottomHole) coords.push(wb.bottomHole.coordinates);
        });
      }
      if (coords.length === 0 && result.developmentAreaName) {
        const unit = unitByName[result.developmentAreaName];
        if (unit?.geometry) {
          coords.push(...extractCoordinatesFromGeometry(unit.geometry));
        }
      }
      if (coords.length > 0) lookup[result.uniqueSlotId] = coords;
    });
    return lookup;
  }, [results, wells, units]);

  // Fit the map to the property picked in the Properties list: reveal the
  // section, scroll it into view, and fly to the slot's geometry. Fires once
  // per request (nonce) so progressive well loads don't re-zoom while the user
  // pans; if geometry isn't loaded yet, a later coords update handles it.
  useEffect(() => {
    if (!zoomRequest || zoomRequest.nonce === handledZoomNonce.current) return;
    if (!mapRef) return;
    const coords = coordsBySlotId[zoomRequest.slotId];
    if (!coords || coords.length === 0) return;
    handledZoomNonce.current = zoomRequest.nonce;
    setExpanded(true);
    containerRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    try {
      const bounds = calculateBounds(coords);
      mapRef.fitBounds(
        [
          [bounds.getWest(), bounds.getSouth()],
          [bounds.getEast(), bounds.getNorth()],
        ],
        { padding: VIEWPORT_PADDING, maxZoom: MAX_ZOOM, duration: 700 }
      );
    } catch {
      // Ignore invalid geometry.
    }
  }, [zoomRequest, mapRef, coordsBySlotId]);

  const mapViewState = useMemo(() => {
    const coords: number[][] = [];
    wells.forEach(({ well }) => {
      (well.wellBores ?? []).forEach((wb) => {
        if (wb.surfaceHole) {
          coords.push([
            wb.surfaceHole.coordinates[0],
            wb.surfaceHole.coordinates[1],
          ]);
        }
        if (wb.bottomHole) {
          coords.push([
            wb.bottomHole.coordinates[0],
            wb.bottomHole.coordinates[1],
          ]);
        }
      });
    });
    units.forEach(({ unit }) => {
      coords.push(...extractCoordinatesFromGeometry(unit.geometry));
    });

    if (coords.length === 0) return null;

    try {
      const bounds = calculateBounds(coords);
      const width = containerRef.current?.clientWidth ?? 900;
      const mercator = new WebMercatorViewport({ width, height: MAP_HEIGHT });
      const viewState = mercator.fitBounds(
        [
          [bounds.getWest(), bounds.getSouth()],
          [bounds.getEast(), bounds.getNorth()],
        ],
        { padding: VIEWPORT_PADDING }
      );
      return {
        longitude: viewState.longitude,
        latitude: viewState.latitude,
        zoom: Math.min(viewState.zoom, MAX_ZOOM),
      };
    } catch {
      return null;
    }
  }, [wells, units]);

  const truncated = totalWithUwi > requestedCount;

  return (
    <Accordion
      expanded={expanded}
      onClick={() => setExpanded(!expanded)}
      className={style.card}
    >
      <AccordionSummary>
        <h3 className={style.heading}>Map</h3>
      </AccordionSummary>
      <AccordionDetails>
        <div ref={containerRef} className={style.container}>
          {mapViewState ? (
            <Map
              longitude={mapViewState.longitude}
              latitude={mapViewState.latitude}
              zoom={mapViewState.zoom}
              mapConfigAtom={mapConfigAtom}
              refAtom={mapRefAtom}
              showControls
              showMapFilters
              scopedLayers={scopedLayers}
            >
              {spatialFilter === 'extent' && (
                <VisibleExtentEmitter onChange={setExtentPolygon} />
              )}
              {spatialFilter === 'aoi' && (
                <DrawAoiControl
                  polygon={aoiPolygon}
                  onChange={setAoiPolygon}
                  editing={aoiEditing}
                  onEditingChange={setAoiEditing}
                />
              )}
              {showUnits &&
                units.map(({ name, unit }) => (
                  <PolygonArea
                    key={`unit-${name}`}
                    id={`cmp-unit-${name}`}
                    geometry={unit.geometry}
                    style={UNIT_STYLE}
                    showBorder
                  />
                ))}
              {visibleWells
                .filter(({ matchStatus }) => wellVisibility[matchStatus])
                .map(({ uwi, matchStatus, well }) =>
                  (well.wellBores ?? []).map((wb, idx) => (
                    <WellMapLayer
                      key={`${uwi}-${wb.uwi14 ?? idx}`}
                      id={`cmp-well-${uwi}-${idx}`}
                      wellbore={wb}
                      mapConfigAtom={mapConfigAtom}
                      colorOverride={wellColor(uwi, matchStatus)}
                    />
                  ))
                )}
            </Map>
          ) : (
            <div className={style.placeholder}>
              <InlineLoading
                description={
                  isLoading ? 'Loading comparison…' : 'Loading well geometry…'
                }
              />
            </div>
          )}
        </div>
        {filterPolygon ? (
          <p className={style.note}>
            Showing {format0(visibleWells.length)} of {format0(wells.length)}{' '}
            wells {spatialFilter === 'extent' ? 'in view' : 'in the drawn area'}
            .
          </p>
        ) : (
          truncated && (
            <p className={style.note}>
              Showing {format0(requestedCount)} of {format0(totalWithUwi)}{' '}
              wells.
            </p>
          )
        )}
      </AccordionDetails>
    </Accordion>
  );
};
